import trackEvent from "components/tracking/trackEvent";
import { useQuery } from "@apollo/client";
import Icon from "components/Icons/Icon";
import { CircleFlag, MONTH_KEYS, REGION_ORDER, TRIP_LENGTHS, displayCountryName, localizedMonths, regionOf } from "components/b2c/explore/exploreCommon";
import { VIBES_MAP } from "components/b2c/explore/exploreAssets";
import { EXPLORE_COUNTRIES } from "graphql/queries/explore";
import { t } from "i18n/index";
import { discoverPath } from "i18n/urlLocale";
import { ArrowUp, Search, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getChromeTranslations } from "../marketing/i18n/pages/chrome";
import "./ExploreSearchOverlay.scss";

// Tilt angles for the result cards — same playful "pinned photo" style as the homepage floating icons.
const TILTS = [-2, 1.5, -1, 2, -1.5, 1, -2.5, 1.5, -1];
const SKELETON_COUNT = 8;

type ExploreCountry = {
	_id: string;
	slug: string;
	name: string;
	tagline?: string;
	heroImage?: string;
	label?: string;
	vibe?: string[];
	/** Trip types gathered from the country's guide-bearing destinations — see `typesOf` below. */
	destinationVibes?: string[];
	bestMonths?: string[];
	destinationCount?: number;
};

function ExploreSearchPill({ label, active, icon, onClick }: { label: string; active: boolean; icon?: string; onClick: () => void }) {
	return (
		<button type="button" className={`explore-search-overlay__pill ${active ? "active" : ""}`} onClick={onClick}>
			{icon && <Icon name={icon as any} size={13} />}
			{label}
		</button>
	);
}

function FilterSection({ label, children }: { label: string; children: React.ReactNode }) {
	return (
		<div className="explore-search-overlay__filter-section">
			<p className="explore-search-overlay__filter-label">{label}</p>
			{children}
		</div>
	);
}

// Singular/plural choice stays `n === 1`, exactly as the hardcoded English did. `t()` does no
// interpolation, so `{count}` is filled here.
function countLabel(tr: Record<string, string>, n: number, noun: "countries" | "destinations", oneFallback: string, otherFallback: string) {
	const key = `exploreSearch.${noun}.${n === 1 ? "one" : "other"}`;
	return t(tr, key, n === 1 ? oneFallback : otherFallback).replace("{count}", String(n));
}

function DestCard({ country, index, lang, tr }: { country: ExploreCountry; index: number; lang: string; tr: Record<string, string> }) {
	const tilt = TILTS[index % TILTS.length];
	const name = displayCountryName(country.name, lang);
	const badge = country.label === "Must-see" ? "popular" : country.label === "Hidden gem" ? "new" : null;

	// `position` because a result chosen from rank 1 and rank 40 are different signals about
	// whether the ordering is any good.
	const onSelect = () => {
		try {
			trackEvent("search_destination_discover", { destination: country.slug, position: index + 1 }, "discover");
		} catch {
			// tracking must never block navigation
		}
	};

	return (
		// `/explore/:countrySlug` is a PrivateRoute — logged-in only. This overlay lives on the
		// marketing homepage, where the visitor is by definition logged out, so every result
		// linked into the authenticated app and bounced them. The public hub is `/discover`.
		// 86cbbc8pm.
		<a href={discoverPath(`/discover/${country.slug}`, lang)} onClick={onSelect} className="explore-search-overlay__card" style={{ "--tilt": `${tilt}deg` } as React.CSSProperties}>
			<img
				className="explore-search-overlay__card-img"
				src={country.heroImage}
				alt={name}
				loading="lazy"
				onError={(e) => {
					e.currentTarget.style.visibility = "hidden";
				}}
			/>
			<div className="explore-search-overlay__card-gradient" />

			{/* One wrapping row rather than two absolutely positioned pills: translated badges
			    ("Nicht verpassen", "Incontournable") are twice the width of "Must-see" and slid
			    under a long country name. When both don't fit, the badge drops to its own line. */}
			<div className="explore-search-overlay__card-top">
				<div className="explore-search-overlay__card-country">
					<CircleFlag country={country.name} className="explore-search-overlay__card-flag" size={20} />
					<span>{name}</span>
				</div>

				{badge && (
					<span className={`explore-search-overlay__card-tag explore-search-overlay__card-tag--${badge}`}>
						{/* `badge` is detected on the English API value above; only the displayed text is localised. */}
						{t(tr, badge === "popular" ? "exploreSearch.badge.mustSee" : "exploreSearch.badge.hiddenGem", country.label)}
					</span>
				)}
			</div>

			<div className="explore-search-overlay__card-text">
				<p className="explore-search-overlay__card-name">{name}</p>
				{country.tagline && <p className="explore-search-overlay__card-subtitle">{country.tagline}</p>}
			</div>
		</a>
	);
}

function DestCardSkeleton({ index }: { index: number }) {
	const tilt = TILTS[index % TILTS.length];
	return <div className="explore-search-overlay__skeleton-card" style={{ "--tilt": `${tilt}deg` } as React.CSSProperties} />;
}

export interface ExploreSearchOverlayProps {
	isOpen: boolean;
	query: string;
	onQueryChange: (v: string) => void;
	onClose: () => void;
	inputRef: React.RefObject<HTMLInputElement>;
	lang?: string;
}

const ExploreSearchOverlay: React.FC<ExploreSearchOverlayProps> = ({ isOpen, query, onQueryChange, onClose, inputRef, lang = "en" }) => {
	const [activeRegions, setActiveRegions] = useState<string[]>([]);
	const [activeMonths, setActiveMonths] = useState<string[]>([]);
	const [activeTypes, setActiveTypes] = useState<string[]>([]);
	const [activeTripLengths, setActiveTripLengths] = useState<string[]>([]);
	const [showScrollTop, setShowScrollTop] = useState(false);
	// Mobile only: When?/Type/Trip-length collapse behind the filter icon (like Discover's BrowsePanel).
	const [showMoreFilters, setShowMoreFilters] = useState(false);
	const overlayRef = useRef<HTMLDivElement>(null);
	const panelRef = useRef<HTMLDivElement>(null);

	// Only hit the API once the overlay is actually opened.
	// `withGuide` restricts results to geos whose page actually exists. Without it this offered
	// all ~4,300 published countries and destinations, most of which have no guide and render
	// the SPA shell. HeroSearchCard's search was given this filter and the overlay was missed —
	// same fix, the other component. 86cbbc279.
	const { data, loading } = useQuery(EXPLORE_COUNTRIES, { variables: { withGuide: true, limit: 200, language: lang }, skip: !isOpen });
	const countries: ExploreCountry[] = data?.exploreCountries || [];

	const monthsShort = useMemo(() => localizedMonths(lang, "short"), [lang]);

	// Marketing-site component: no react-i18next here. Per-surface dictionary + plain `t()`, the
	// same pairing Navigation.tsx uses. Only the DISPLAYED text is localised; the English region
	// name (`regionOf`), vibe `key` and trip-length `key` stay the identity in state and filtering.
	const tr = useMemo(() => getChromeTranslations(lang), [lang]);
	const regionLabel = (r: string) => t(tr, `exploreSearch.region.${r}`, r);

	// A country's own `vibe` is a curated headline of exactly two entries, not an inventory of
	// what it offers. All 17 countries with a guide have two, and not one of them is `beach` —
	// Thailand is `spiritual, culinary`, Indonesia `nature, spiritual`. So the "Beach" chip
	// matched nothing while every other chip matched something (123yb692y91).
	//
	// `destinationVibes` is what its destinations actually are, counted over the ones that have
	// a guide, so the union answers the question the chip asks: can I go there for this.
	const typesOf = useCallback((c: ExploreCountry) => new Set([...(c.vibe || []), ...(c.destinationVibes || [])]), []);

	// Only offer a chip something can match, the same way the region strip already works. Without
	// this, "Skiing" is the next "Beach": no destination with a guide carries `ski`, so the chip
	// would sit there returning nothing, and the bug would be reported again under a new name.
	const availableTypes = useMemo(() => {
		const present = new Set<string>();
		for (const c of countries) for (const v of typesOf(c)) present.add(v);
		return Object.entries(VIBES_MAP).filter(([key]) => present.has(key));
	}, [countries, typesOf]);

	const availableRegions = useMemo(() => {
		const present = new Set(countries.map((c) => regionOf(c.name)));
		const ordered = REGION_ORDER.filter((r) => present.has(r));
		const extras = [...present].filter((r) => !REGION_ORDER.includes(r)).sort();
		return [...ordered, ...extras];
	}, [countries]);

	useEffect(() => {
		if (!isOpen) return undefined;

		document.body.style.overflow = "hidden";
		const handleClickOutside = (e: MouseEvent) => {
			const target = e.target as Element;
			if (!overlayRef.current?.contains(target) && !target.closest?.("[data-search-pill]")) onClose();
		};
		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === "Escape") onClose();
		};
		document.addEventListener("mousedown", handleClickOutside);
		window.addEventListener("keydown", handleKeyDown);

		return () => {
			document.body.style.overflow = "";
			document.removeEventListener("mousedown", handleClickOutside);
			window.removeEventListener("keydown", handleKeyDown);
		};
	}, [isOpen, onClose]);

	useEffect(() => {
		if (!isOpen) return;
		setActiveRegions([]);
		setActiveMonths([]);
		setActiveTypes([]);
		setActiveTripLengths([]);
		setShowScrollTop(false);
		setShowMoreFilters(false);
		onQueryChange("");
	}, [isOpen, onQueryChange]);

	const toggle = useCallback((arr: string[], val: string, set: (v: string[]) => void) => set(arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val]), []);

	const hasFilters = !!(activeRegions.length || activeMonths.length || activeTypes.length || activeTripLengths.length);
	const moreFiltersCount = activeMonths.length + activeTypes.length + activeTripLengths.length;

	const filtered = countries.filter((c) => {
		const q = query.trim().toLowerCase();
		const name = displayCountryName(c.name, lang).toLowerCase();
		const destinationCount = c.destinationCount || 0;
		return (
			(!q || name.includes(q) || (c.tagline || "").toLowerCase().includes(q)) &&
			(!activeRegions.length || activeRegions.includes(regionOf(c.name))) &&
			(!activeTypes.length || activeTypes.some((v) => typesOf(c).has(v))) &&
			(!activeMonths.length || activeMonths.some((m) => (c.bestMonths || []).includes(m))) &&
			(!activeTripLengths.length ||
				activeTripLengths.some((key) => {
					const bucket = TRIP_LENGTHS.find((len) => len.key === key);
					return bucket && destinationCount >= bucket.min && destinationCount <= bucket.max;
				}))
		);
	});

	const totalDestinations = filtered.reduce((sum, c) => sum + (c.destinationCount || 0), 0);

	// Hooks must run in the same order on every render, so this sits above the early return
	// below rather than beside the markup it belongs to. ESLint's rules-of-hooks caught it;
	// `tsc` cannot, which is why the typecheck passed locally and CI still failed.
	//
	// Debounced so a typed word is one event, not one per keystroke. A query that matches
	// nothing is the reason this exists: we gate this list to curated geos (86cbbbh8e,
	// 86cbbc279) and had no measure of the demand we were turning away.
	const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
	useEffect(() => {
		const q = query.trim();
		if (searchTimer.current) clearTimeout(searchTimer.current);
		// The overlay keeps its query when closed; without this it would report a search
		// nobody is looking at.
		if (!isOpen || q.length < 2) return undefined;
		searchTimer.current = setTimeout(() => {
			try {
				trackEvent("search_discover", { query: q.toLowerCase(), results: filtered.length, has_results: filtered.length > 0, filtered: hasFilters }, "discover");
			} catch {
				// tracking must never affect search
			}
		}, 700);
		return () => {
			if (searchTimer.current) clearTimeout(searchTimer.current);
		};
	}, [query, filtered.length, hasFilters, isOpen]);

	if (!isOpen) return null;



	const clearFilters = () => {
		setActiveRegions([]);
		setActiveMonths([]);
		setActiveTypes([]);
		setActiveTripLengths([]);
	};

	return (
		<>
			<div className="explore-search-overlay__backdrop" onMouseDown={onClose} />
			<div className="explore-search-overlay" ref={overlayRef}>
				<div
					className="explore-search-overlay__panel"
					ref={panelRef}
					onScroll={(e) => setShowScrollTop(e.currentTarget.scrollTop > 320)}
				>
					<div className="explore-search-overlay__sidebar">
						<div className="explore-search-overlay__search">
							<Search size={15} />
							<input
								ref={inputRef}
								value={query}
								onChange={(e) => onQueryChange(e.target.value)}
								placeholder={t(tr, "exploreSearch.placeholder", "Search destination...")}
								autoFocus
							/>
							{query && (
								<button
									type="button"
									className="explore-search-overlay__search-clear"
									onClick={() => onQueryChange("")}
									aria-label={t(tr, "exploreSearch.clearSearch", "Clear search")}
								>
									<X size={13} />
								</button>
							)}
						</div>

						{/* Mobile only (CSS-toggled): compact filter bar — filter icon + horizontal region strip, à la BrowsePanel. */}
						<div className="explore-search-overlay__filter-bar">
							<button
								type="button"
								className={`explore-search-overlay__filter-toggle ${showMoreFilters ? "active" : ""}`}
								onClick={() => setShowMoreFilters((v) => !v)}
								aria-label={showMoreFilters ? t(tr, "exploreSearch.hideFilters", "Hide filters") : t(tr, "exploreSearch.moreFilters", "More filters")}
							>
								<Icon name="filter" size={14} pathProps={{ stroke: showMoreFilters ? "#003f4c" : "#ffffff" }} />
								{moreFiltersCount > 0 && <span className="explore-search-overlay__filter-count">{moreFiltersCount}</span>}
							</button>
							{hasFilters && (
								<button
									type="button"
									className="explore-search-overlay__filter-clear-icon"
									onClick={clearFilters}
									aria-label={t(tr, "exploreSearch.clearFilters", "Clear filters")}
								>
									<X size={12} />
								</button>
							)}
							<div className="explore-search-overlay__region-strip">
								{availableRegions.map((r) => (
									<ExploreSearchPill
										key={r}
										label={regionLabel(r)}
										active={activeRegions.includes(r)}
										onClick={() => toggle(activeRegions, r, setActiveRegions)}
									/>
								))}
							</div>
						</div>

						{/* Desktop only (CSS-toggled): Region as a normal wrapping pill section. */}
						<div className="explore-search-overlay__desktop-region">
							<FilterSection label={t(tr, "exploreSearch.filter.region", "Region")}>
								<div className="explore-search-overlay__pills">
									{availableRegions.map((r) => (
										<ExploreSearchPill
											key={r}
											label={regionLabel(r)}
											active={activeRegions.includes(r)}
											onClick={() => toggle(activeRegions, r, setActiveRegions)}
										/>
									))}
								</div>
							</FilterSection>
						</div>

						{/* Always shown on desktop; collapsed behind the filter icon on mobile. */}
						<div className={`explore-search-overlay__more-filters ${showMoreFilters ? "open" : ""}`}>
							<FilterSection label={t(tr, "exploreSearch.filter.when", "When?")}>
								<div className="explore-search-overlay__months">
									{monthsShort.map((label, i) => {
										const key = MONTH_KEYS[i];
										return (
											<button
												key={key}
												type="button"
												className={`explore-search-overlay__month ${activeMonths.includes(key) ? "active" : ""}`}
												onClick={() => toggle(activeMonths, key, setActiveMonths)}
											>
												{label}
											</button>
										);
									})}
								</div>
							</FilterSection>

							<FilterSection label={t(tr, "exploreSearch.filter.type", "Type of trip")}>
								<div className="explore-search-overlay__pills">
									{availableTypes.map(([key, v]) => (
										<ExploreSearchPill
											key={key}
											label={t(tr, `exploreSearch.vibe.${key}`, v.name)}
											icon={v.icon}
											active={activeTypes.includes(key)}
											onClick={() => toggle(activeTypes, key, setActiveTypes)}
										/>
									))}
								</div>
							</FilterSection>

							<FilterSection label={t(tr, "exploreSearch.filter.tripLength", "Trip length")}>
								<div className="explore-search-overlay__trip-lengths">
									{TRIP_LENGTHS.map((len) => (
										<button
											key={len.key}
											type="button"
											className={`explore-search-overlay__trip-length ${activeTripLengths.includes(len.key) ? "active" : ""}`}
											onClick={() => toggle(activeTripLengths, len.key, setActiveTripLengths)}
										>
											{t(tr, `exploreSearch.tripLength.${len.key}`, len.label)}
										</button>
									))}
								</div>
							</FilterSection>
						</div>

						{hasFilters && (
							<button type="button" className="explore-search-overlay__clear-filters" onClick={clearFilters}>
								{t(tr, "exploreSearch.clearFilters", "Clear filters")}
							</button>
						)}
					</div>

					<div className="explore-search-overlay__results">
						<div className="explore-search-overlay__results-header">
							{loading ? (
								<div className="explore-search-overlay__results-header-skeleton" />
							) : (
								<p>
									{countLabel(tr, filtered.length, "countries", "{count} country", "{count} countries")}
									<span className="explore-search-overlay__results-dot">·</span>
									{countLabel(tr, totalDestinations, "destinations", "{count} destination", "{count} destinations")}
									{query && <span className="explore-search-overlay__results-query"> · "{query}"</span>}
								</p>
							)}
							<button type="button" className="explore-search-overlay__close" onClick={onClose} aria-label={t(tr, "exploreSearch.close", "Close search")}>
								<X size={13} />
							</button>
						</div>

						{loading ? (
							<div className="explore-search-overlay__grid">
								{Array.from({ length: SKELETON_COUNT }).map((_, i) => (
									<DestCardSkeleton key={i} index={i} />
								))}
							</div>
						) : filtered.length === 0 ? (
							<div className="explore-search-overlay__empty">
								<div className="explore-search-overlay__empty-icon">
									<Search size={20} />
								</div>
								<p>{t(tr, "exploreSearch.empty.title", "No destinations found")}</p>
								<span>{t(tr, "exploreSearch.empty.hint", "Try different filters or a new search")}</span>
							</div>
						) : (
							<div className="explore-search-overlay__grid">
								{filtered.map((country, i) => (
									<DestCard key={country.slug} country={country} index={i} lang={lang} tr={tr} />
								))}
							</div>
						)}
					</div>
				</div>

				{showScrollTop && (
					<button
						type="button"
						className="explore-search-overlay__scroll-top"
						onClick={() => panelRef.current?.scrollTo({ top: 0, behavior: "smooth" })}
						aria-label={t(tr, "exploreSearch.scrollToTop", "Scroll to top")}
					>
						<ArrowUp size={18} />
					</button>
				)}
			</div>
		</>
	);
};

export default ExploreSearchOverlay;
